
import React, { useRef, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { toast } from 'sonner';

const loader = new Loader({
  apiKey: 'AIzaSyCWaBm2SJuzqpEp-0QL0gKUEu49naUxYnc',
  version: 'weekly',
  libraries: ['places']
});

interface MapContainerProps {
  defaultLocation: google.maps.LatLngLiteral;
  address: string;
  shouldAnalyze: boolean;
  searchTrigger: number;
  parties: Array<{
    type: string;
    name: string;
    address: string;
  }>;
  onLocationAnalysis: (analysis: { accessibility: string; footTraffic: string; demographics: string }) => void;
  onMarkerClick: (party: { type: string; name: string; address: string }) => void;
  isLoaded: boolean;
  setIsLoaded: (loaded: boolean) => void;
}

export const MapContainer: React.FC<MapContainerProps> = ({
  defaultLocation,
  address,
  shouldAnalyze,
  searchTrigger,
  parties,
  onLocationAnalysis,
  onMarkerClick,
  isLoaded,
  setIsLoaded
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const partyMarkers = useRef<google.maps.Marker[]>([]);
  const infoWindows = useRef<google.maps.InfoWindow[]>([]);

  const getMarkerColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'customer': return '#50C878';
      case 'distributor': return '#0EA5E9';
      case 'reseller': return '#8B4513';
      case 'competitor': return '#ea384c';
      default: return '#000000';
    }
  };

  const createMarkerIcon = (color: string) => {
    return {
      path: 'M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z',
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: '#000000',
      scale: 1.5,
      anchor: new google.maps.Point(12, 24)
    };
  };

  const clearPartyMarkers = () => {
    partyMarkers.current.forEach(marker => marker.setMap(null));
    partyMarkers.current = [];
    infoWindows.current.forEach(infoWindow => infoWindow.close());
    infoWindows.current = [];
  };

  const analyzeLocation = async (location: google.maps.LatLng) => {
    if (!map.current) return;
    
    try {
      const placesService = new google.maps.places.PlacesService(map.current);
      
      const searchPromise = (request: google.maps.places.PlaceSearchRequest): Promise<google.maps.places.PlaceResult[]> => {
        return new Promise((resolve, reject) => {
          placesService.nearbySearch(request, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
              resolve(results);
            } else {
              reject(status);
            }
          });
        });
      };

      const [transitPlaces, commercialPlaces, generalPlaces] = await Promise.all([
        searchPromise({ location, radius: 500, type: 'transit_station' }),
        searchPromise({ location, radius: 500, type: 'store' }),
        searchPromise({ location, radius: 500, type: 'establishment' })
      ]);

      const analysis = {
        accessibility: transitPlaces.length > 3 ? 'Excellent' : transitPlaces.length > 0 ? 'Good' : 'Limited',
        footTraffic: commercialPlaces.length > 10 ? 'High' : commercialPlaces.length > 5 ? 'Moderate' : 'Low',
        demographics: 'Mixed'
      };

      const demographicTypes = new Set<string>();
      generalPlaces.forEach(place => {
        if (!place.types) return;
        if (place.types.includes('school') || place.types.includes('university')) {
          demographicTypes.add('Students');
        }
        if (place.types.includes('shopping_mall') || place.types.includes('store')) {
          demographicTypes.add('Shoppers');
        }
        if (place.types.includes('office')) {
          demographicTypes.add('Professionals');
        }
        if (place.types.includes('residential')) {
          demographicTypes.add('Residents');
        }
      });

      if (demographicTypes.size > 0) {
        analysis.demographics = Array.from(demographicTypes).join(', ');
      }

      onLocationAnalysis(analysis);
    } catch (error) {
      console.error('Error analyzing location:', error);
      onLocationAnalysis({
        accessibility: 'Error analyzing',
        footTraffic: 'Error analyzing',
        demographics: 'Error analyzing'
      });
      toast.error('Error analyzing location');
    }
  };

  const addPartyMarkers = async () => {
    if (!map.current) return;
    clearPartyMarkers();
    
    const geocoder = new google.maps.Geocoder();
    for (const party of parties) {
      try {
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode({ address: party.address }, (results, status) => {
            if (status === 'OK' && results) {
              resolve(results);
            } else {
              reject(status);
            }
          });
        });

        if (result[0].geometry.location) {
          const markerColor = getMarkerColor(party.type);
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="color: #1A1F2C; font-size: 14px;">
                <strong>Type:</strong> ${party.type}<br>
                <strong>Name:</strong> ${party.name || 'N/A'}<br>
                <strong>Location:</strong> ${party.address}
              </div>
            `
          });

          const newMarker = new google.maps.Marker({
            position: result[0].geometry.location,
            map: map.current,
            icon: createMarkerIcon(markerColor)
          });

          newMarker.addListener('mouseover', () => {
            infoWindow.open(map.current, newMarker);
          });

          newMarker.addListener('mouseout', () => {
            infoWindow.close();
          });

          newMarker.addListener('click', () => {
            onMarkerClick(party);
          });

          partyMarkers.current.push(newMarker);
          infoWindows.current.push(infoWindow);
        }
      } catch (error) {
        console.error('Error geocoding address:', party.address, error);
        toast.error(`Failed to locate: ${party.address}`);
      }
    }
  };

  useEffect(() => {
    if (!mapContainer.current) return;
    
    loader.load().then(() => {
      setIsLoaded(true);
      if (!map.current && mapContainer.current) {
        map.current = new google.maps.Map(mapContainer.current, {
          center: defaultLocation,
          zoom: 14,
          mapTypeId: 'roadmap',
          mapTypeControl: false,
          scrollwheel: true,
          gestureHandling: 'greedy'
        });

        if (!address) {
          marker.current = new google.maps.Marker({
            map: map.current,
            position: defaultLocation
          });
          analyzeLocation(new google.maps.LatLng(defaultLocation.lat, defaultLocation.lng));
        }
      }
    }).catch(error => {
      console.error('Error loading Google Maps:', error);
      toast.error('Error loading Google Maps');
    });

    return () => {
      if (marker.current) {
        marker.current.setMap(null);
        marker.current = null;
      }
      clearPartyMarkers();
    };
  }, [defaultLocation]);

  useEffect(() => {
    if (!isLoaded || !map.current) return;
    
    if (!address) {
      map.current.setCenter(defaultLocation);
      if (marker.current) {
        marker.current.setPosition(defaultLocation);
      } else {
        marker.current = new google.maps.Marker({
          map: map.current,
          position: defaultLocation
        });
      }

      if (shouldAnalyze) {
        analyzeLocation(new google.maps.LatLng(defaultLocation.lat, defaultLocation.lng));
      }
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0] && map.current) {
        const location = results[0].geometry.location;
        map.current.setCenter(location);
        map.current.setZoom(14);
        
        if (marker.current) {
          marker.current.setMap(null);
        }
        
        marker.current = new google.maps.Marker({
          map: map.current,
          position: location
        });

        if (shouldAnalyze) {
          analyzeLocation(location);
        }
      } else {
        console.error('Geocoding error:', status);
        toast.error('Error finding location. Please check the address.');
      }
    });
  }, [address, isLoaded, defaultLocation, shouldAnalyze, searchTrigger]);

  useEffect(() => {
    if (isLoaded && map.current && parties.length > 0) {
      addPartyMarkers();
    }
  }, [isLoaded, parties]);

  return <div ref={mapContainer} className="w-full h-full" />;
};
